import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaInfoCircle } from "react-icons/fa";
import { MdClose, MdWarningAmber } from "react-icons/md";
import { BsCheck } from "react-icons/bs";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { DetailedCitizenScoresIndicatorC3Data } from "@/services/esus/indicator/esf/c3/getDetailedCitizensScoresIndicatorC3";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";
import { getCitizenScoreStatusDescriptionIndicatorC3 } from "@/utils/indicatorsEsf";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsfC3CitizensScoresTableColumns = {
	citizenName: Column;
	healthTeam: Column;
	microarea: Column;
	firstAttendanceStatus: Column;
	sevenPregnancyAttendancesStatus: Column;
	sevenBloodPressureMeasurementsStatus: Column;
	sevenMetricsRegistersStatus: Column;
	threeVisitsStatus: Column;
	vaccinationStatus: Column;
	firstTrimesterProceduresStatus: Column;
	thirdTrimesterProceduresStatus: Column;
	puerperalAttendancesStatus: Column;
	puerperalVisitsStatus: Column;
	dentalAttendancesStatus: Column;
	score: Column;
};

type IndicatorEsfC3CitizensScoresTableProps = {
	citizensScores: DetailedCitizenScoresIndicatorC3Data[];
	isLoading: boolean;
};

export function getIconByCitizenScoreStatusIndicatorC3(
	citizenScoreStatus: "finished" | "pending" | "lost"
) {
	switch (citizenScoreStatus) {
		case "finished":
			return (
				<BsCheck
					className="text-primary fs-2"
					title={getCitizenScoreStatusDescriptionIndicatorC3(citizenScoreStatus)}
				/>
			);
		case "pending":
			return (
				<MdWarningAmber
					className="text-warning fs-2"
					title={getCitizenScoreStatusDescriptionIndicatorC3(citizenScoreStatus)}
				/>
			);
		case "lost":
			return (
				<MdClose
					className="text-danger fs-2"
					title={getCitizenScoreStatusDescriptionIndicatorC3(citizenScoreStatus)}
				/>
			);
	}
}

export function IndicatorEsfC3CitizensScoresTable({
	citizensScores,
	isLoading,
}: Readonly<IndicatorEsfC3CitizensScoresTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC3CitizensScoresTableColumns>({
		citizenName: { title: "Cidadão", visible: true },
		healthTeam: { title: "Equipe", visible: false },
		microarea: { title: "MA", visible: false },
		firstAttendanceStatus: { title: "1ª Consulta", visible: true },
		sevenPregnancyAttendancesStatus: { title: "Consultas", visible: true },
		sevenBloodPressureMeasurementsStatus: { title: "Afer. PA", visible: true },
		sevenMetricsRegistersStatus: { title: "Peso/Altura", visible: true },
		threeVisitsStatus: { title: "Visitas", visible: true },
		vaccinationStatus: { title: "Vacinações", visible: true },
		firstTrimesterProceduresStatus: { title: "Proced. 1º tri", visible: true },
		thirdTrimesterProceduresStatus: { title: "Proced. 3º tri", visible: true },
		puerperalAttendancesStatus: { title: "Cons. puerpério", visible: true },
		puerperalVisitsStatus: { title: "Visitas puerpério", visible: true },
		dentalAttendancesStatus: { title: "Cons. odonto", visible: true },
		score: { title: "Pontos", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th
						className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}
						style={{ minWidth: 200 }}
					>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.firstAttendanceStatus.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.firstAttendanceStatus.title}
							<CardOverlayTrigger
								header="1ª Consulta"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Gestante com a 1ª Consulta presencial realizada até <br />
									12 semanas de gestação <br />
									(Prática A - 10 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.sevenPregnancyAttendancesStatus.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.sevenPregnancyAttendancesStatus.title}
							<CardOverlayTrigger
								header="Consultas"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Gestante com consultas realizadas por médico ou enfermeiro{" "}
									<br />
									(a portaria indica que sejam realizadas 7 consultas) <br />
									(Prática B - 9 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.sevenBloodPressureMeasurementsStatus.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.sevenBloodPressureMeasurementsStatus.title}
							<CardOverlayTrigger
								header="Registros de aferição de PA"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Gestante com registros de aferição de PA (a portaria indica{" "}
									<br />
									que sejam realizados 7 registros) <br />
									(Prática C - 9 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.sevenMetricsRegistersStatus.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.sevenMetricsRegistersStatus.title}
							<CardOverlayTrigger
								header="Registros de peso e altura"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Gestante com registros de peso e altura (a portaria indica{" "}
									<br />
									que sejam realizados 7 registros) <br />
									(Prática D - 9 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>

					<th className={`px-2 ${!columns.threeVisitsStatus.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.threeVisitsStatus.title}
							<CardOverlayTrigger
								header="Visitas"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Gestante com visitas realizadas por ACS ou TACS com a informação
									<br />
									de motivo da visita preenchida como 'Acompanhamento de Gestante'
									<br />
									(a portaria indica que sejam realizadas 3 visitas no período{" "}
									<br />
									gestacional) <br />
									(Prática E - 9 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className={`px-2 ${!columns.vaccinationStatus.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.vaccinationStatus.title}
							<CardOverlayTrigger
								header="Vacinações"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Gestante com esquema vacinal em dias que tenha sido imunizado
									<br />
									contra difteria, tétano e coqueluche com todas as doses <br />
									recomendadas (a portaria indica que seja aplicada 1 dose a{" "}
									<br />
									partir da 20ª semana de gestação) <br />
									(Prática F - 9 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.firstTrimesterProceduresStatus.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.firstTrimesterProceduresStatus.title}
							<CardOverlayTrigger
								header="Procedimentos 1º Trimestre"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Gestante com procedimentos avaliados ou realizados no 1º
									trimestre <br />
									(a portaria indica que seja exame ou teste de hiv, sífilis,{" "}
									<br />
									hepatite B e hepatite C durante o 1º trimestre de gestação){" "}
									<br />
									(Prática G - 9 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.thirdTrimesterProceduresStatus.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.thirdTrimesterProceduresStatus.title}
							<CardOverlayTrigger
								header="Procedimentos 3º Trimestre"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Gestante com procedimentos avaliados ou realizados no 1º
									trimestre <br />
									(a portaria indica que seja exame ou teste de hiv e sífilis,{" "}
									<br />
									hepatite b, hepatite c durante o 3º trimestre de gestação){" "}
									<br />
									(Prática H - 9 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.puerperalAttendancesStatus.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.puerperalAttendancesStatus.title}
							<CardOverlayTrigger
								header="Consulta - puerpério"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Gestante com consulta realizada durante o período puerperal{" "}
									<br />
									(Prática I - 9 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className={`px-2 ${!columns.puerperalVisitsStatus.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.puerperalVisitsStatus.title}
							<CardOverlayTrigger
								header="Visita - puerpério"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Gestante com visita domiciliar realizada durante o período
									puerperal <br />
									(Prática J - 9 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className={`px-2 ${!columns.dentalAttendancesStatus.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.dentalAttendancesStatus.title}
							<CardOverlayTrigger
								header="Consulta - odontológica"
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									Gestante com consulta odontológica realizada durante todo o
									período gestacional <br />
									(Prática K - 9 pontos)
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className={`px-2 ${!columns.score.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.score.title}{" "}
							<CardOverlayTrigger
								header="Pontuação"
								message="Para cada prática existe uma pontuação específica, onde a soma de todas as práticas totaliza 100 pontos por cidadão"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC3CitizensScoresTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC3CitizensScoresTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC3CitizensScoresTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensScores.length > 0
					? citizensScores.map((citizenScores, index) => (
							<tr
								className="text-center"
								key={`health-team-classification-table-row-${index + 1}`}
							>
								<td
									className={`text-start px-2 ${
										!columns.citizenName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{citizenScores.fullName}</div>
										<div className="text-muted">
											{dayjs(citizenScores.birthdate).format("DD/MM/YYYY")}
										</div>
									</div>
								</td>
								<td className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenScores.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{citizenScores.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenScores.area ?? "FA"}
								</td>
								<td
									className={`px-2 ${
										!columns.firstAttendanceStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC3(
										citizenScores.firstAttendanceStatus
									)}
								</td>
								<td
									className={`px-2 ${
										!columns.sevenPregnancyAttendancesStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC3(
										citizenScores.sevenPregnancyAttendancesStatus
									)}
								</td>
								<td
									className={`px-2 ${
										!columns.sevenBloodPressureMeasurementsStatus.visible &&
										"d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC3(
										citizenScores.sevenBloodPressureMeasurementsStatus
									)}
								</td>
								<td
									className={`px-2 ${
										!columns.sevenMetricsRegistersStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC3(
										citizenScores.sevenWeightAndHeightMetricsRegistersStatus
									)}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.threeVisitsStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC3(
										citizenScores.threeAcsHomeVisitsStatus
									)}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.vaccinationStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC3(
										citizenScores.vaccinationStatus
									)}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.firstTrimesterProceduresStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC3(
										citizenScores.firstTrimesterProceduresStatus
									)}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.thirdTrimesterProceduresStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC3(
										citizenScores.thirdTrimesterProceduresStatus
									)}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.puerperalAttendancesStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC3(
										citizenScores.puerperalAttendanceStatus
									)}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.puerperalVisitsStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC3(
										citizenScores.puerperalAcsHomeVisitStatus
									)}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.dentalAttendancesStatus.visible && "d-none"
									}`}
								>
									{getIconByCitizenScoreStatusIndicatorC3(
										citizenScores.dentalAttendancesStatus
									)}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.score.visible && "d-none"
									}`}
								>
									{citizenScores.score}
								</td>
								<td></td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
